import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import flatMap from 'lodash/flatMap';
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import { readFileSync } from 'fs';
import pathData from '../../buildArtifacts/paths';
import Layout from '../../components/Layout';
import Breadcrumb from '../../components/Breadcrumb';
import PageLiveCourse from '../../components/PageLiveCourse';
import PageLiveCoursePropTypes from '../../components/PageLiveCourse/PageLiveCoursePropTypes';
import getBreadcrumb from '../../utils/getBreadcrumb';
import tidySeo from '../../utils/tidySeo';
import getSeoPageTitle from '../../utils/getSeoPageTitle';
import getCoveoSearchMeta from '../../utils/getCoveoSearchMeta';
import getCanonicalUrl from '../../utils/getCanonicalUrl';
import getSettings from '../../buildArtifacts/settings';

const { pageLiveCourse: paths } = pathData;

export const pageLiveCoursePropTypes = {
  pageData: PropTypes.shape(PageLiveCoursePropTypes).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  seo: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  breadcrumbs: PropTypes.arrayOf(PropTypes.object).isRequired,
  pageTitle: PropTypes.string,
  canonicalUrl: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  settingsGlobal: PropTypes.object.isRequired
};

function LiveCoursePage({ pageData, seo, breadcrumbs, pageTitle, canonicalUrl, settingsGlobal }) {
  const {
    _id,
    _href,
    _as,
    _contentTypeId,
    category,
    image,
    thumbnail,
    name,
    description,
    slug,
    courseHighlights,
    duration,
    languageZoomIDs,
    registrationConfirmationHeader,
    registrationConfirmationCopy,
    jsonLdSchema
  } = pageData;

  const router = useRouter();

  const {
    query: { rescheduling_webinar_id: reschedulingWebinarId, rescheduling_occurrence_id: reschedulingOccurrenceId }
  } = router;

  return (
    <>
      <Layout
        seo={seo}
        showFooter={false}
        pageTitle={pageTitle}
        canonicalUrl={canonicalUrl}
        settingsGlobal={settingsGlobal}>
        <Breadcrumb breadcrumbs={breadcrumbs} />

        <PageLiveCourse
          _id={_id}
          _href={_href}
          _as={_as}
          _contentTypeId={_contentTypeId}
          category={category}
          image={image}
          thumbnail={thumbnail}
          slug={slug}
          name={name}
          description={description}
          courseHighlights={courseHighlights}
          duration={duration}
          languageZoomIDs={languageZoomIDs}
          registrationConfirmationHeader={registrationConfirmationHeader}
          registrationConfirmationCopy={registrationConfirmationCopy}
          reschedulingWebinarId={reschedulingWebinarId}
          reschedulingOccurrenceId={reschedulingOccurrenceId}
        />

        {jsonLdSchema && !isEmpty(jsonLdSchema) && (
          // eslint-disable-next-line react/no-danger
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }} />
        )}
      </Layout>
    </>
  );
}

// This function gets called at build time
export const getStaticPaths = async ({ locales }) => ({
  paths: flatMap(locales, (locale) => paths.map((pathObject) => ({ ...pathObject, locale }))),
  fallback: false
});

export const getStaticProps = async ({ locale, params }) => {
  const settingsGlobal = getSettings(locale);
  const { slug } = params;

  const pageLearningLiveData = JSON.parse(
    readFileSync(`./src/buildArtifacts/contentJson/${locale}/pageLanding/virtual-training.json`, 'utf8')
  );

  const pageData = JSON.parse(
    readFileSync(`./src/buildArtifacts/contentJson/${locale}/pageLiveCourse/${slug}.json`, 'utf8')
  );

  const pageDataSeo = tidySeo(get(pageData, 'seo', {}));
  const pageTitle = getSeoPageTitle(pageDataSeo, pageData);

  const canonicalUrl = getCanonicalUrl(pageData, locale, `virtual-training/${slug}`);

  delete pageDataSeo.title;

  const pageCoveoSearchMeta = getCoveoSearchMeta(pageData, locale);
  const seo = { ...pageDataSeo, ...pageCoveoSearchMeta };
  seo.content_type = { name: 'content_type', value: pageData._contentTypeId };

  const breadcrumbs = [getBreadcrumb(pageLearningLiveData), getBreadcrumb(pageData)];

  return {
    props: {
      pageData,
      seo,
      breadcrumbs,
      pageTitle,
      canonicalUrl,
      settingsGlobal
    }
  };
};

LiveCoursePage.propTypes = pageLiveCoursePropTypes;

LiveCoursePage.defaultProps = {
  seo: {},
  pageTitle: ''
};

export default LiveCoursePage;
